import {
  productTheme
} from "./chunk-7AV5FO4O.mjs";

// src/product/theme.ts
import React, { createContext, useContext } from "react";
var ProductThemeContext = createContext(productTheme);
var ProductThemeProvider = ({
  children,
  theme = productTheme
}) => {
  return React.createElement(
    ProductThemeContext.Provider,
    { value: theme },
    children
  );
};
var useProductTheme = () => {
  return useContext(ProductThemeContext);
};

// src/product/post-card/ProductPostCard.tsx
import {
  useEffect as useEffect2,
  useId,
  useMemo as useMemo2,
  useRef,
  useState as useState2
} from "react";
import { ArrowSquareOut, LockSimple } from "@phosphor-icons/react";

// src/product/avatar/Avatar.tsx
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useState
} from "react";
import { UserCircle } from "@phosphor-icons/react";
import { jsx, jsxs } from "react/jsx-runtime";
var cx = (...values) => values.filter(Boolean).join(" ");
var sizeVar = (size) => `var(--lc-avatar-size-${size})`;
function AvatarStyles() {
  return /* @__PURE__ */ jsx("style", { "data-lc-avatar-styles": "", children: AVATAR_CSS });
}
function deriveAvatarInitials(name, size = "lg") {
  const value = (name ?? "").trim().replace(/^@+/, "");
  if (!value) return "?";
  const compactSize = size === "2xs" || size === "xs";
  const parts = value.split(/[\s._-]+/).filter(Boolean);
  if (parts.length > 1 && !compactSize) {
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "?";
  }
  const letters = parts[0] ?? value;
  return letters.slice(0, compactSize ? 1 : 2).toUpperCase();
}
function fallbackChild(children) {
  let found = null;
  Children.forEach(children, (child) => {
    if (found || !isValidElement(child)) return;
    const props = child.props;
    if (!("kind" in props)) {
      found = child;
    }
  });
  return found;
}
function badgeChildren(children) {
  const badges = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const props = child.props;
    if ("kind" in props) {
      badges.push(child);
    }
  });
  return badges;
}
function Avatar({
  src,
  sources,
  alt,
  name,
  initials,
  icon,
  decorative,
  size = "lg",
  responsiveSize,
  shape = "circle",
  fit = "cover",
  tone = "neutral",
  status = "default",
  loading = false,
  disabled = false,
  selected = false,
  locked = false,
  imageLoading = "lazy",
  decoding = "async",
  onImageError,
  fallbackDelayMs = 0,
  children,
  className,
  style,
  ...rest
}) {
  const [failedSrcs, setFailedSrcs] = useState(/* @__PURE__ */ new Set());
  const [showFallback, setShowFallback] = useState(fallbackDelayMs <= 0);
  const activeSrc = src && !failedSrcs.has(src) ? src : null;
  const fallback = fallbackChild(children);
  const badges = badgeChildren(children);
  const effectiveDecorative = decorative ?? (!alt && !name);
  const label = effectiveDecorative ? void 0 : alt || name;
  const rootStyle = {
    "--lc-avatar-size": sizeVar(size),
    ...responsiveSize?.base ? { "--lc-avatar-size": sizeVar(responsiveSize.base) } : null,
    ...responsiveSize?.sm ? { "--lc-avatar-size-sm": sizeVar(responsiveSize.sm) } : null,
    ...responsiveSize?.md ? { "--lc-avatar-size-md": sizeVar(responsiveSize.md) } : null,
    ...responsiveSize?.lg ? { "--lc-avatar-size-lg": sizeVar(responsiveSize.lg) } : null,
    ...responsiveSize?.xl ? { "--lc-avatar-size-xl": sizeVar(responsiveSize.xl) } : null,
    ...style
  };
  const fallbackText = initials || deriveAvatarInitials(name || alt, size);
  const fallbackNode = fallback ? cloneElement(fallback, { "aria-hidden": true }) : fallbackText !== "?" ? /* @__PURE__ */ jsx("span", { className: "lc-avatar-initials", children: fallbackText }) : icon ?? /* @__PURE__ */ jsx(UserCircle, { "aria-hidden": "true", weight: "regular" });
  useEffect(() => {
    if (fallbackDelayMs <= 0) {
      setShowFallback(true);
      return;
    }
    setShowFallback(false);
    const timer = window.setTimeout(() => setShowFallback(true), fallbackDelayMs);
    return () => window.clearTimeout(timer);
  }, [fallbackDelayMs, activeSrc, loading]);
  const picture = useMemo(() => {
    if (!activeSrc || loading) return null;
    return /* @__PURE__ */ jsxs("picture", { className: "lc-avatar-picture", children: [
      sources?.map((source) => /* @__PURE__ */ jsx("source", { srcSet: source.srcSet ?? source.src, type: source.type }, `${source.type ?? "source"}:${source.src}`)),
      /* @__PURE__ */ jsx(
        "img",
        {
          className: "lc-avatar-image",
          src: activeSrc,
          alt: effectiveDecorative ? "" : alt ?? name ?? "",
          loading: imageLoading,
          decoding,
          onError: () => {
            setFailedSrcs((prev) => new Set(prev).add(activeSrc));
            onImageError?.(activeSrc);
          }
        }
      )
    ] });
  }, [activeSrc, alt, decoding, effectiveDecorative, imageLoading, name, onImageError, sources, loading]);
  return /* @__PURE__ */ jsxs(
    "span",
    {
      ...rest,
      className: cx("lc-avatar", `lc-avatar-size-${size}`, `lc-avatar-shape-${shape}`, `lc-avatar-fit-${fit}`, `lc-avatar-tone-${tone}`, className),
      style: rootStyle,
      "aria-label": loading ? rest["aria-label"] : label,
      "aria-hidden": loading && !rest["aria-label"] ? true : effectiveDecorative || void 0,
      "aria-busy": loading || void 0,
      "data-status": loading ? "loading" : locked ? "locked" : disabled ? "disabled" : selected ? "selected" : status,
      "data-loading": loading ? "true" : "false",
      "data-disabled": disabled ? "true" : void 0,
      "data-selected": selected ? "true" : void 0,
      "data-locked": locked ? "true" : void 0,
      children: [
        /* @__PURE__ */ jsx(AvatarStyles, {}),
        picture,
        !picture ? /* @__PURE__ */ jsx("span", { className: "lc-avatar-content", "aria-hidden": "true", children: loading ? /* @__PURE__ */ jsx("span", { className: "lc-avatar-skeleton" }) : showFallback ? fallbackNode : null }) : null,
        badges
      ]
    }
  );
}
var AVATAR_CSS = `
.lc-avatar {
  --lc-avatar-size-2xs: var(--storm-spacing-4);
  --lc-avatar-size-xs: var(--storm-spacing-5);
  --lc-avatar-size-sm: var(--storm-spacing-6);
  --lc-avatar-size-md: var(--storm-spacing-7, calc(var(--storm-spacing-6) + var(--storm-spacing-1)));
  --lc-avatar-size-lg: var(--storm-spacing-8);
  --lc-avatar-size-xl: var(--storm-spacing-10);
  --lc-avatar-size-2xl: var(--storm-spacing-14);
  --lc-avatar-size-hero: var(--storm-spacing-24);
  --lc-avatar-bg: var(--storm-surface-container-high, var(--storm-color-surface-container-high, Canvas));
  --lc-avatar-fg: var(--storm-text-secondary, var(--storm-color-on-surface-variant, CanvasText));
  --lc-avatar-ring: var(--storm-outline-variant, var(--storm-color-outline-variant, currentColor));
  position: relative;
  display: inline-flex;
  width: var(--lc-avatar-size);
  height: var(--lc-avatar-size);
  min-width: var(--lc-avatar-size);
  min-height: var(--lc-avatar-size);
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 auto;
  background: var(--lc-avatar-bg);
  color: var(--lc-avatar-fg);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--lc-avatar-ring) 70%, transparent);
  font: 600 max(10px, calc(var(--lc-avatar-size) * 0.36)) / 1 var(--storm-font-sans, inherit);
  letter-spacing: 0;
  vertical-align: middle;
}
.lc-avatar-shape-circle { border-radius: var(--storm-radius-full, 9999px); }
.lc-avatar-shape-rounded { border-radius: var(--storm-radius-md, 8px); }
.lc-avatar-shape-square { border-radius: var(--storm-radius-xs, 2px); }
.lc-avatar-picture,
.lc-avatar-image,
.lc-avatar-content,
.lc-avatar-skeleton {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.lc-avatar-image {
  display: block;
  object-position: center;
}
.lc-avatar-fit-cover .lc-avatar-image { object-fit: cover; }
.lc-avatar-fit-contain .lc-avatar-image { object-fit: contain; padding: max(1px, calc(var(--lc-avatar-size) * 0.12)); }
.lc-avatar-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.lc-avatar-skeleton {
  background: color-mix(in srgb, var(--lc-avatar-ring) 30%, transparent);
}
.lc-avatar-initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  text-transform: uppercase;
}
.lc-avatar-tone-accent { --lc-avatar-fg: var(--storm-primary, var(--storm-color-primary, CanvasText)); }
.lc-avatar-tone-metric { --lc-avatar-fg: var(--storm-metric, var(--storm-color-primary, CanvasText)); }
.lc-avatar-tone-muted { opacity: 0.72; }
.lc-avatar[data-status='selected'] { box-shadow: inset 0 0 0 2px var(--storm-primary, currentColor); }
.lc-avatar[data-status='disabled'] { opacity: 0.48; }
.lc-avatar[data-status='locked'] { filter: grayscale(1); }
.lc-avatar-fallback {
  display: inline-flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
}
.lc-avatar-badge {
  --lc-avatar-badge-size: max(var(--storm-spacing-3, 12px), calc(var(--lc-avatar-size) * 0.34));
  position: absolute;
  z-index: 1;
  display: inline-flex;
  width: var(--lc-avatar-badge-size);
  height: var(--lc-avatar-badge-size);
  min-width: var(--lc-avatar-badge-size);
  min-height: var(--lc-avatar-badge-size);
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid var(--storm-surface, Canvas);
  border-radius: var(--storm-radius-full, 9999px);
  background: var(--storm-surface-container-highest, var(--lc-avatar-bg));
  color: var(--storm-text-primary, CanvasText);
  font: 600 max(8px, calc(var(--lc-avatar-badge-size) * 0.52)) / 1 var(--storm-font-sans, inherit);
}
.lc-avatar-badge-xs { --lc-avatar-badge-size: var(--storm-spacing-2, 8px); }
.lc-avatar-badge-sm { --lc-avatar-badge-size: var(--storm-spacing-3, 12px); }
.lc-avatar-badge-md { --lc-avatar-badge-size: var(--storm-spacing-4, 16px); }
.lc-avatar-badge-top-right { top: 0; right: 0; }
.lc-avatar-badge-bottom-right { right: 0; bottom: 0; }
.lc-avatar-badge-bottom-left { bottom: 0; left: 0; }
.lc-avatar-badge-top-left { top: 0; left: 0; }
.lc-avatar-group {
  --lc-avatar-group-size: var(--lc-avatar-size-md);
  display: inline-flex;
  height: var(--lc-avatar-group-size);
  min-height: var(--lc-avatar-group-size);
  align-items: center;
  vertical-align: middle;
}
.lc-avatar-group[data-size='2xs'] { --lc-avatar-group-size: var(--lc-avatar-size-2xs); }
.lc-avatar-group[data-size='xs'] { --lc-avatar-group-size: var(--lc-avatar-size-xs); }
.lc-avatar-group[data-size='sm'] { --lc-avatar-group-size: var(--lc-avatar-size-sm); }
.lc-avatar-group[data-size='lg'] { --lc-avatar-group-size: var(--lc-avatar-size-lg); }
.lc-avatar-group[data-size='xl'] { --lc-avatar-group-size: var(--lc-avatar-size-xl); }
.lc-avatar-group[data-size='2xl'] { --lc-avatar-group-size: var(--lc-avatar-size-2xl); }
.lc-avatar-group[data-size='hero'] { --lc-avatar-group-size: var(--lc-avatar-size-hero); }
.lc-avatar-group-item,
.lc-avatar-group-overflow {
  display: inline-flex;
  width: var(--lc-avatar-group-size);
  height: var(--lc-avatar-group-size);
  min-width: var(--lc-avatar-group-size);
  min-height: var(--lc-avatar-group-size);
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: var(--storm-radius-full, 9999px);
  background: var(--storm-surface-container-high, Canvas);
  color: inherit;
  text-decoration: none;
  appearance: none;
  -webkit-appearance: none;
  font: inherit;
}
.lc-avatar-group button.lc-avatar-group-item,
button.lc-avatar-group-overflow { cursor: pointer; }
.lc-avatar-group [aria-disabled='true'] { cursor: default; }
.lc-avatar-group-overlap .lc-avatar-group-item + .lc-avatar-group-item,
.lc-avatar-group-overlap .lc-avatar-group-overflow {
  margin-left: calc(var(--storm-spacing-2, 8px) * -1);
}
.lc-avatar-group-tight { gap: var(--storm-spacing-1, 4px); }
.lc-avatar-group-even {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, var(--lc-avatar-group-size));
  justify-content: space-between;
}
.lc-avatar-group-item-avatar,
.lc-avatar-group-overflow {
  box-shadow: 0 0 0 1px var(--storm-surface, Canvas);
}
.lc-avatar-group-overflow {
  z-index: 2;
  font: 600 max(9px, calc(var(--lc-avatar-group-size) * 0.32)) / 1 var(--storm-font-sans, inherit);
}
@media (min-width: 640px) {
  .lc-avatar { width: var(--lc-avatar-size-sm, var(--lc-avatar-size)); height: var(--lc-avatar-size-sm, var(--lc-avatar-size)); min-width: var(--lc-avatar-size-sm, var(--lc-avatar-size)); min-height: var(--lc-avatar-size-sm, var(--lc-avatar-size)); }
}
@media (min-width: 768px) {
  .lc-avatar { width: var(--lc-avatar-size-md, var(--lc-avatar-size-sm, var(--lc-avatar-size))); height: var(--lc-avatar-size-md, var(--lc-avatar-size-sm, var(--lc-avatar-size))); min-width: var(--lc-avatar-size-md, var(--lc-avatar-size-sm, var(--lc-avatar-size))); min-height: var(--lc-avatar-size-md, var(--lc-avatar-size-sm, var(--lc-avatar-size))); }
}
@media (min-width: 1024px) {
  .lc-avatar { width: var(--lc-avatar-size-lg, var(--lc-avatar-size-md, var(--lc-avatar-size-sm, var(--lc-avatar-size)))); height: var(--lc-avatar-size-lg, var(--lc-avatar-size-md, var(--lc-avatar-size-sm, var(--lc-avatar-size)))); min-width: var(--lc-avatar-size-lg, var(--lc-avatar-size-md, var(--lc-avatar-size-sm, var(--lc-avatar-size)))); min-height: var(--lc-avatar-size-lg, var(--lc-avatar-size-md, var(--lc-avatar-size-sm, var(--lc-avatar-size)))); }
}
@media (min-width: 1280px) {
  .lc-avatar { width: var(--lc-avatar-size-xl, var(--lc-avatar-size-lg, var(--lc-avatar-size-md, var(--lc-avatar-size-sm, var(--lc-avatar-size))))); height: var(--lc-avatar-size-xl, var(--lc-avatar-size-lg, var(--lc-avatar-size-md, var(--lc-avatar-size-sm, var(--lc-avatar-size))))); min-width: var(--lc-avatar-size-xl, var(--lc-avatar-size-lg, var(--lc-avatar-size-md, var(--lc-avatar-size-sm, var(--lc-avatar-size))))); min-height: var(--lc-avatar-size-xl, var(--lc-avatar-size-lg, var(--lc-avatar-size-md, var(--lc-avatar-size-sm, var(--lc-avatar-size))))); }
}
`;

// src/product/avatar/AvatarFallback.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var cx2 = (...values) => values.filter(Boolean).join(" ");
function AvatarFallback({ initials, icon, label, tone = "neutral", className, children, ...rest }) {
  return /* @__PURE__ */ jsx2(
    "span",
    {
      ...rest,
      className: cx2("lc-avatar-fallback", `lc-avatar-fallback-${tone}`, className),
      role: label ? "img" : void 0,
      "aria-label": label,
      "data-avatar-fallback": "",
      children: children ?? icon ?? initials
    }
  );
}

// src/product/avatar/AvatarBadge.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var cx3 = (...values) => values.filter(Boolean).join(" ");
function AvatarBadge({
  kind,
  position = "bottom-right",
  size = "auto",
  label,
  decorative = false,
  interactive = false,
  pressed,
  disabled,
  children,
  className,
  ...rest
}) {
  const classes = cx3("lc-avatar-badge", `lc-avatar-badge-${position}`, `lc-avatar-badge-${size}`, `lc-avatar-badge-kind-${kind}`, className);
  if (interactive) {
    const buttonProps = rest;
    return /* @__PURE__ */ jsx3(
      "button",
      {
        ...buttonProps,
        type: buttonProps.type ?? "button",
        className: classes,
        "aria-label": label,
        "aria-pressed": pressed,
        disabled,
        "data-avatar-badge": "",
        children
      }
    );
  }
  return /* @__PURE__ */ jsx3(
    "span",
    {
      ...rest,
      className: classes,
      "aria-label": decorative ? void 0 : label,
      "aria-hidden": decorative || void 0,
      "data-avatar-badge": "",
      children
    }
  );
}

// src/product/avatar/AvatarGroup.tsx
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var cx4 = (...values) => values.filter(Boolean).join(" ");
function AvatarGroup({
  items,
  size = "md",
  max = 5,
  spacing = "overlap",
  // Contract section 456: the overflow control label must include hidden AND total
  // counts (e.g. "Show all 37"), so a screen reader announces the full set, not
  // just the remainder. Consumers override with an entity-specific noun.
  overflowLabel = (hiddenCount, totalCount) => `${hiddenCount} more of ${totalCount}`,
  interactive = false,
  disabled = false,
  className,
  ...rest
}) {
  const visible = items.slice(0, Math.max(0, max));
  const hiddenCount = Math.max(0, items.length - visible.length);
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      ...rest,
      className: cx4("lc-avatar-group", `lc-avatar-group-${spacing}`, className),
      "data-avatar-group": "",
      "data-size": size,
      "data-disabled": disabled ? "true" : void 0,
      "aria-disabled": disabled || void 0,
      children: [
        visible.map((item) => {
          const content = /* @__PURE__ */ jsx4("span", { className: "lc-avatar-group-item-avatar", children: item.avatar });
          const itemClassName = "lc-avatar-group-item";
          if (interactive && item.href && !disabled && !item.disabled) {
            return /* @__PURE__ */ jsx4("a", { className: itemClassName, href: item.href, "aria-label": item.label, children: content }, item.id);
          }
          if (interactive && !disabled && !item.disabled) {
            return /* @__PURE__ */ jsx4("button", { className: itemClassName, type: "button", "aria-label": item.label, children: content }, item.id);
          }
          return /* @__PURE__ */ jsx4("span", { className: itemClassName, "aria-label": item.label, "aria-disabled": disabled || item.disabled || void 0, children: content }, item.id);
        }),
        hiddenCount > 0 ? interactive && !disabled ? /* @__PURE__ */ jsxs2("button", { className: "lc-avatar-group-overflow", type: "button", "aria-label": overflowLabel(hiddenCount, items.length), children: [
          "+",
          hiddenCount
        ] }) : /* @__PURE__ */ jsxs2("span", { className: "lc-avatar-group-overflow", "aria-label": overflowLabel(hiddenCount, items.length), children: [
          "+",
          hiddenCount
        ] }) : null
      ]
    }
  );
}

// src/product/post-card/ProductPostCard.tsx
import { jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var cx5 = (...values) => values.filter(Boolean).join(" ");
var cssValue = (value, fallback) => value == null ? fallback : typeof value === "number" ? `${value}px` : value;
var toneClass = (tone = "neutral") => `lc-product-post-card-tone-${tone}`;
function ProductPostCardStyles() {
  return /* @__PURE__ */ jsx5("style", { "data-product-post-card-styles": "", children: PRODUCT_POST_CARD_CSS });
}
function ProductPostCardRoot({
  layout = "grid",
  href,
  external = false,
  interactive = true,
  linkLabel = "Open post",
  height,
  mediaHeight,
  loading = false,
  locked = false,
  lockedLabel = "Locked content",
  children,
  className,
  style,
  ...rest
}) {
  const labelId = useId();
  const slotsRef = useRef(null);
  const uniform = layout !== "masonry";
  const mergedStyle = {
    ...height == null ? null : { "--lc-post-card-height": cssValue(height, "420px") },
    ...mediaHeight == null ? null : { "--lc-post-card-media-height": cssValue(mediaHeight, "132px") },
    ...style
  };
  const linked = interactive && Boolean(href) && !locked && !loading;
  useEffect2(() => {
    const slots = slotsRef.current;
    if (!slots) return;
    if (loading || locked) slots.setAttribute("inert", "");
    else slots.removeAttribute("inert");
  }, [loading, locked]);
  return /* @__PURE__ */ jsxs3(
    "article",
    {
      ...rest,
      className: cx5("lc-product-post-card", `lc-product-post-card-${layout}`, interactive && "is-interactive", locked && "is-locked", className),
      style: mergedStyle,
      "aria-busy": loading || void 0,
      "aria-labelledby": linked ? labelId : void 0,
      "data-layout": layout,
      "data-uniform-height": uniform ? "true" : "false",
      "data-loading": loading ? "true" : "false",
      "data-locked": locked ? "true" : "false",
      children: [
        /* @__PURE__ */ jsx5(ProductPostCardStyles, {}),
        linked ? /* @__PURE__ */ jsx5(
          "a",
          {
            id: labelId,
            className: "lc-product-post-card-link",
            href,
            target: external ? "_blank" : void 0,
            rel: external ? "noopener noreferrer" : void 0,
            "aria-label": external ? `${linkLabel} (opens in a new tab)` : linkLabel,
            children: /* @__PURE__ */ jsx5("span", { className: "lc-product-post-card-sr-only", children: external ? `${linkLabel}, opens in a new tab` : linkLabel })
          }
        ) : null,
        /* @__PURE__ */ jsx5("div", { ref: slotsRef, className: "lc-product-post-card-slots", "aria-hidden": loading || locked ? true : void 0, children }),
        external && linked ? /* @__PURE__ */ jsx5("span", { className: "lc-product-post-card-external", "aria-hidden": "true", children: /* @__PURE__ */ jsx5(ArrowSquareOut, { size: 13 }) }) : null,
        loading ? /* @__PURE__ */ jsxs3("div", { className: "lc-product-post-card-loading", role: "status", "aria-label": "Loading post", children: [
          /* @__PURE__ */ jsx5("i", {}),
          /* @__PURE__ */ jsx5("i", {}),
          /* @__PURE__ */ jsx5("i", {}),
          /* @__PURE__ */ jsx5("i", {})
        ] }) : null,
        locked ? /* @__PURE__ */ jsxs3("div", { className: "lc-product-post-card-locked", role: "status", children: [
          /* @__PURE__ */ jsx5(LockSimple, { "aria-hidden": "true" }),
          /* @__PURE__ */ jsx5("span", { children: lockedLabel })
        ] }) : null
      ]
    }
  );
}
function ProductPostCardHeader({
  avatar,
  avatarSrc,
  avatarAlt,
  avatarFallback,
  displayName,
  handle,
  timestamp,
  networkIcon,
  action,
  className,
  ...rest
}) {
  const fallback = avatarFallback ?? (typeof displayName === "string" ? displayName.trim().slice(0, 1).toUpperCase() : "\u2022");
  return /* @__PURE__ */ jsxs3("header", { ...rest, className: cx5("lc-product-post-card-header", className), "data-post-card-slot": "header", children: [
    /* @__PURE__ */ jsx5("div", { className: "lc-product-post-card-avatar", children: avatar ?? /* @__PURE__ */ jsx5(Avatar, { src: avatarSrc, alt: avatarAlt, name: typeof displayName === "string" ? displayName : avatarAlt, size: "lg", decorative: !avatarAlt, children: /* @__PURE__ */ jsx5(AvatarFallback, { label: avatarAlt || void 0, children: fallback }) }) }),
    /* @__PURE__ */ jsxs3("div", { className: "lc-product-post-card-author", children: [
      /* @__PURE__ */ jsxs3("div", { className: "lc-product-post-card-author-line", children: [
        /* @__PURE__ */ jsx5("strong", { children: displayName }),
        networkIcon ? /* @__PURE__ */ jsx5("span", { className: "lc-product-post-card-network", children: networkIcon }) : null
      ] }),
      handle || timestamp ? /* @__PURE__ */ jsxs3("div", { className: "lc-product-post-card-byline", children: [
        handle,
        /* @__PURE__ */ jsx5("span", { "aria-hidden": "true", children: "\xB7" }),
        timestamp
      ] }) : null
    ] }),
    action ? /* @__PURE__ */ jsx5("div", { className: "lc-product-post-card-action", children: action }) : null
  ] });
}
function ProductPostCardContent({
  clamp,
  centered = false,
  scrollable = false,
  scrollLabel = "Scrollable post content",
  scrollFade = true,
  children,
  className,
  style,
  onScroll,
  ...rest
}) {
  const viewportRef = useRef(null);
  const [canScrollDown, setCanScrollDown] = useState2(false);
  const updateOverflow = () => {
    const viewport = viewportRef.current;
    if (!viewport || !scrollable) return setCanScrollDown(false);
    setCanScrollDown(viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop > 1);
  };
  useEffect2(() => {
    updateOverflow();
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(viewport);
    if (viewport.firstElementChild) observer.observe(viewport.firstElementChild);
    return () => observer.disconnect();
  }, [children, scrollable]);
  const mergedStyle = {
    ...clamp && !scrollable ? { "--lc-post-card-content-clamp": clamp } : null,
    ...style
  };
  return /* @__PURE__ */ jsx5(
    "div",
    {
      ...rest,
      ref: viewportRef,
      className: cx5("lc-product-post-card-content", centered && "is-centered", scrollable && "is-scrollable", scrollFade && "has-scroll-fade", Boolean(clamp && !scrollable) && "is-clamped", className),
      style: mergedStyle,
      tabIndex: scrollable ? 0 : rest.tabIndex,
      role: scrollable ? "region" : rest.role,
      "aria-label": scrollable ? scrollLabel : rest["aria-label"],
      "data-can-scroll-down": canScrollDown ? "true" : "false",
      "data-post-card-slot": "content",
      onScroll: (event) => {
        updateOverflow();
        onScroll?.(event);
      },
      children: /* @__PURE__ */ jsx5("div", { className: "lc-product-post-card-content-inner", children })
    }
  );
}
function ProductPostCardMedia({
  kind = "image",
  src,
  alt,
  poster,
  aspectRatio = "16 / 9",
  fit = "cover",
  fallback = "Media unavailable",
  hideOnError = false,
  className,
  style,
  ...rest
}) {
  const [failed, setFailed] = useState2(false);
  useEffect2(() => setFailed(false), [src]);
  if (failed && hideOnError) return null;
  return /* @__PURE__ */ jsx5("div", { ...rest, className: cx5("lc-product-post-card-media", className), style: { aspectRatio, ...style }, "data-post-card-slot": "media", children: !src || failed ? /* @__PURE__ */ jsx5("div", { className: "lc-product-post-card-media-fallback", role: "img", "aria-label": alt, children: fallback }) : kind === "video" ? /* @__PURE__ */ jsx5("video", { src, poster, "aria-label": alt, preload: "metadata", muted: true, playsInline: true, onError: () => setFailed(true), style: { objectFit: fit } }) : /* @__PURE__ */ jsx5("img", { src, alt, onError: () => setFailed(true), style: { objectFit: fit } }) });
}
function ProductPostCardAnalytics({ children, className, ...rest }) {
  return /* @__PURE__ */ jsx5("div", { ...rest, className: cx5("lc-product-post-card-analytics", className), "data-post-card-slot": "analytics", children });
}
function ProductPostCardInsight({ icon, label, tone = "info", className, ...rest }) {
  return /* @__PURE__ */ jsxs3("div", { ...rest, className: cx5("lc-product-post-card-insight", toneClass(tone), className), "data-post-card-slot": "insight", children: [
    icon,
    /* @__PURE__ */ jsx5("span", { children: label })
  ] });
}
function linePoints(values) {
  if (values.length < 2) return "";
  const min = Math.min(...values);
  const max = Math.max(...values);
  const spread = Math.max(1, max - min);
  return values.map((value, index) => `${index / (values.length - 1) * 100},${28 - (value - min) / spread * 24}`).join(" ");
}
function ProductPostCardSparkline({
  variant = "line",
  values,
  points,
  startLabel,
  endLabel,
  slots,
  rightAligned = false,
  loading = false,
  emptyLabel = "No trend data",
  label = "Post activity trend",
  tone = "positive",
  children,
  className,
  ...rest
}) {
  const preparedPoints = typeof points === "string" ? void 0 : points;
  const resolvedLine = useMemo2(() => typeof points === "string" ? points : linePoints(values ?? preparedPoints?.map((point) => point.value) ?? []), [points, preparedPoints, values]);
  const resolvedBars = preparedPoints ?? values?.map((value) => ({ value }));
  const barSlots = Math.max(slots ?? resolvedBars?.length ?? 0, resolvedBars?.length ?? 0);
  const leadingSlots = rightAligned ? Math.max(0, barSlots - (resolvedBars?.length ?? 0)) : 0;
  const barMax = Math.max(1, ...resolvedBars?.map((point) => point.value) ?? []);
  return /* @__PURE__ */ jsxs3("div", { ...rest, className: cx5("lc-product-post-card-sparkline", `is-${variant}`, toneClass(tone), className), "data-post-card-slot": "sparkline", children: [
    /* @__PURE__ */ jsx5("div", { className: "lc-product-post-card-chart", children: loading ? /* @__PURE__ */ jsx5("div", { className: "lc-product-post-card-sparkline-loading", "aria-label": "Loading trend" }) : children ? children : variant === "bars" && resolvedBars?.length ? /* @__PURE__ */ jsxs3("div", { className: "lc-product-post-card-bars", role: "img", "aria-label": label, style: { "--lc-post-card-bar-slots": barSlots }, children: [
      Array.from({ length: leadingSlots }, (_, index) => /* @__PURE__ */ jsx5("i", { "aria-hidden": "true" }, `empty-${index}`)),
      resolvedBars.map((point, index) => /* @__PURE__ */ jsx5("i", { title: point.label, style: { "--lc-post-card-bar-value": Math.max(0.06, point.value / barMax) } }, `${point.label ?? "bar"}-${index}`))
    ] }) : resolvedLine ? /* @__PURE__ */ jsx5("svg", { viewBox: "0 0 100 32", role: "img", "aria-label": label, preserveAspectRatio: "none", children: /* @__PURE__ */ jsx5("polyline", { points: resolvedLine, vectorEffect: "non-scaling-stroke" }) }) : /* @__PURE__ */ jsx5("span", { className: "lc-product-post-card-empty", children: emptyLabel }) }),
    startLabel != null || endLabel != null ? /* @__PURE__ */ jsxs3("div", { className: "lc-product-post-card-sparkline-labels", children: [
      /* @__PURE__ */ jsx5("span", { children: startLabel }),
      /* @__PURE__ */ jsx5("span", { children: endLabel })
    ] }) : null
  ] });
}
function ProductPostCardMetrics({ metrics, emptyLabel = "No metrics available", labelVisibility = "sr-only", className, ...rest }) {
  const present = metrics.filter((metric) => metric.value != null);
  return /* @__PURE__ */ jsx5("dl", { ...rest, className: cx5("lc-product-post-card-metrics", className), "data-post-card-slot": "metrics", children: present.length ? present.map((metric) => {
    const visible = metric.showLabel ?? labelVisibility === "visible";
    return /* @__PURE__ */ jsxs3("div", { className: cx5("lc-product-post-card-metric", visible && "has-visible-label"), children: [
      metric.icon,
      /* @__PURE__ */ jsx5("dd", { children: metric.value }),
      /* @__PURE__ */ jsx5("dt", { children: metric.label })
    ] }, metric.id);
  }) : /* @__PURE__ */ jsx5("div", { className: "lc-product-post-card-empty", children: emptyLabel }) });
}
function ProductPostCardIndicators({ indicators, emptyLabel = "No indicators", className, ...rest }) {
  return /* @__PURE__ */ jsx5("ul", { ...rest, className: cx5("lc-product-post-card-indicators", className), "data-post-card-slot": "indicators", children: indicators.length ? indicators.map((indicator) => {
    const marker = indicator.marker === false ? null : indicator.marker ?? /* @__PURE__ */ jsx5("i", { className: "lc-product-post-card-indicator-marker", "aria-hidden": "true" });
    const iconBefore = indicator.icon && indicator.iconPosition !== "after-label";
    return /* @__PURE__ */ jsxs3("li", { className: toneClass(indicator.tone), children: [
      marker,
      iconBefore ? indicator.icon : null,
      /* @__PURE__ */ jsx5("span", { children: indicator.label }),
      !iconBefore ? indicator.icon : null,
      indicator.value != null ? /* @__PURE__ */ jsx5("strong", { children: indicator.value }) : null
    ] }, indicator.id);
  }) : /* @__PURE__ */ jsx5("li", { className: "lc-product-post-card-empty", children: emptyLabel }) });
}
var ProductPostCard = Object.assign(ProductPostCardRoot, {
  Header: ProductPostCardHeader,
  Content: ProductPostCardContent,
  Media: ProductPostCardMedia,
  Analytics: ProductPostCardAnalytics,
  Insight: ProductPostCardInsight,
  Sparkline: ProductPostCardSparkline,
  Metrics: ProductPostCardMetrics,
  Indicators: ProductPostCardIndicators
});
var PRODUCT_POST_CARD_CSS = `
.lc-product-post-card{--lc-ppc-surface:var(--card,var(--storm-background-card));--lc-ppc-border:var(--border,var(--storm-border-default));--lc-ppc-text:var(--foreground,var(--storm-text-primary));--lc-ppc-muted:var(--muted-foreground,var(--storm-text-secondary));--lc-ppc-accent:var(--primary,var(--storm-border-brand));--lc-post-card-media-height:132px;position:relative;box-sizing:border-box;width:100%;max-width:100%;min-width:0;color:var(--lc-ppc-text);background:var(--lc-ppc-surface);border:1px solid var(--lc-ppc-border);border-radius:var(--radius-lg,12px);overflow:hidden;isolation:isolate}
.lc-product-post-card-rail{--lc-post-card-height:430px;height:var(--lc-post-card-height)}.lc-product-post-card-grid{--lc-post-card-height:420px;height:var(--lc-post-card-height)}
.lc-product-post-card-slots{position:relative;z-index:2;min-width:0;height:100%;pointer-events:none}
.lc-product-post-card-rail .lc-product-post-card-slots,.lc-product-post-card-grid .lc-product-post-card-slots{display:grid;grid-template-areas:'header' 'content' 'analytics';grid-template-rows:auto minmax(64px,1fr) 132px;gap:10px;padding:14px}
.lc-product-post-card-rail .lc-product-post-card-slots:has(>.lc-product-post-card-media),.lc-product-post-card-grid .lc-product-post-card-slots:has(>.lc-product-post-card-media){grid-template-areas:'header' 'content' 'media' 'analytics';grid-template-rows:auto minmax(64px,1fr) var(--lc-post-card-media-height) 132px}
.lc-product-post-card-masonry .lc-product-post-card-slots{display:flex;flex-direction:column;gap:12px;padding:14px}
.lc-product-post-card-header{grid-area:header;display:flex;align-items:center;min-width:0;gap:10px}
.lc-product-post-card-avatar{--lc-post-card-avatar-size:34px;width:var(--lc-post-card-avatar-size);height:var(--lc-post-card-avatar-size);flex:0 0 var(--lc-post-card-avatar-size);border-radius:999px;overflow:hidden;display:grid;place-items:center}.lc-product-post-card-avatar>.lc-avatar{--lc-avatar-size:var(--lc-post-card-avatar-size);width:var(--lc-post-card-avatar-size);height:var(--lc-post-card-avatar-size);min-width:var(--lc-post-card-avatar-size);min-height:var(--lc-post-card-avatar-size);font-size:12px}
.lc-product-post-card-author{min-width:0;flex:1}.lc-product-post-card-author-line{display:flex;align-items:center;gap:5px;min-width:0}.lc-product-post-card-author-line strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:13px}.lc-product-post-card-network{display:inline-flex;flex:none;color:var(--lc-ppc-muted)}.lc-product-post-card-byline{display:flex;align-items:center;gap:4px;min-width:0;overflow:hidden;color:var(--lc-ppc-muted);font-size:11px;white-space:nowrap;text-overflow:ellipsis}.lc-product-post-card-action{position:relative;z-index:4;flex:none;pointer-events:auto}
.lc-product-post-card-content{grid-area:content;position:relative;min-width:0;min-height:0;overflow-wrap:anywhere;font-size:13px;line-height:1.5}.lc-product-post-card-content-inner{min-width:0}.lc-product-post-card-content.is-centered{display:flex;flex-direction:column}.lc-product-post-card-content.is-centered .lc-product-post-card-content-inner{width:100%;margin-block:auto;text-align:left}.lc-product-post-card-content.is-clamped .lc-product-post-card-content-inner{display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:var(--lc-post-card-content-clamp);overflow:hidden}.lc-product-post-card-content.is-scrollable{overflow:auto;overscroll-behavior:contain;scrollbar-width:thin;scrollbar-color:transparent transparent;pointer-events:auto}.lc-product-post-card-content.is-scrollable::-webkit-scrollbar{width:6px;height:6px}.lc-product-post-card-content.is-scrollable::-webkit-scrollbar-track{background:transparent}.lc-product-post-card-content.is-scrollable::-webkit-scrollbar-thumb{border-radius:999px;background:transparent}.lc-product-post-card-content.is-scrollable:hover,.lc-product-post-card-content.is-scrollable:focus{scrollbar-color:var(--lc-ppc-muted) transparent}.lc-product-post-card-content.is-scrollable:hover::-webkit-scrollbar-thumb,.lc-product-post-card-content.is-scrollable:focus::-webkit-scrollbar-thumb{background:var(--lc-ppc-muted)}.lc-product-post-card-content.is-scrollable:focus-visible{outline:2px solid var(--ring,var(--lc-ppc-accent));outline-offset:-2px;border-radius:4px}.lc-product-post-card-content.has-scroll-fade[data-can-scroll-down=true]::after{content:'';position:sticky;z-index:1;display:block;left:0;right:0;bottom:0;height:22px;margin-top:-22px;pointer-events:none;background:linear-gradient(to bottom,transparent,var(--lc-ppc-surface))}
.lc-product-post-card-media{grid-area:media;min-width:0;min-height:0;overflow:hidden;border-radius:8px;background:var(--muted,var(--storm-background-tertiary))}.lc-product-post-card-rail .lc-product-post-card-media,.lc-product-post-card-grid .lc-product-post-card-media{height:100%;aspect-ratio:auto!important}.lc-product-post-card-media img,.lc-product-post-card-media video{display:block;width:100%;height:100%}.lc-product-post-card-media-fallback{height:100%;display:grid;place-items:center;color:var(--lc-ppc-muted);font-size:11px}
.lc-product-post-card-analytics{grid-area:analytics;min-width:0;min-height:0}.lc-product-post-card-rail .lc-product-post-card-analytics,.lc-product-post-card-grid .lc-product-post-card-analytics{display:grid;grid-template-areas:'insight' 'sparkline' 'metrics' 'indicators';grid-template-rows:24px 42px 28px 20px;gap:4px}.lc-product-post-card-masonry .lc-product-post-card-analytics{display:flex;flex-direction:column;gap:8px}
.lc-product-post-card-insight{grid-area:insight;display:flex;align-items:center;min-width:0;gap:6px;overflow:hidden;font-size:11px;font-weight:600}.lc-product-post-card-insight span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.lc-product-post-card-sparkline{grid-area:sparkline;min-width:0;display:grid;grid-template-rows:minmax(0,1fr) auto;gap:2px}.lc-product-post-card-chart{min-width:0;min-height:0;display:flex;align-items:center}.lc-product-post-card-sparkline svg{width:100%;height:100%;overflow:visible}.lc-product-post-card-sparkline polyline{fill:none;stroke:currentColor;stroke-width:1.75;stroke-linecap:round;stroke-linejoin:round}.lc-product-post-card-bars{width:100%;height:100%;display:grid;grid-template-columns:repeat(var(--lc-post-card-bar-slots),minmax(1px,1fr));align-items:end;gap:2px}.lc-product-post-card-bars i{display:block;min-height:2px;height:calc(var(--lc-post-card-bar-value,0) * 100%);border-radius:2px 2px 0 0;background:currentColor}.lc-product-post-card-bars i:not([style]){visibility:hidden}.lc-product-post-card-sparkline-labels{display:flex;justify-content:space-between;gap:8px;color:var(--lc-ppc-muted);font-size:9px;line-height:1;white-space:nowrap}.lc-product-post-card-sparkline-loading{width:100%;height:18px;border-radius:4px;background:var(--muted,var(--storm-background-tertiary));animation:lc-ppc-pulse 1.4s ease-in-out infinite}
.lc-product-post-card-metrics{grid-area:metrics;display:flex;align-items:center;min-width:0;gap:12px;margin:0;overflow:hidden}.lc-product-post-card-metric{display:flex;align-items:center;min-width:0;gap:4px;color:var(--lc-ppc-muted);font-size:11px}.lc-product-post-card-metric dt{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0)}.lc-product-post-card-metric.has-visible-label dt{position:static;width:auto;height:auto;overflow:visible;clip:auto}.lc-product-post-card-metric dd{margin:0;color:var(--lc-ppc-text);font-variant-numeric:tabular-nums}
.lc-product-post-card-indicators{grid-area:indicators;display:flex;align-items:center;min-width:0;gap:8px;margin:0;padding:0;list-style:none;overflow:hidden}.lc-product-post-card-indicators li{display:flex;align-items:center;min-width:0;gap:4px;font-size:10px;white-space:nowrap}.lc-product-post-card-indicator-marker{width:6px;height:6px;flex:none;border-radius:999px;background:currentColor}.lc-product-post-card-indicators strong{font-weight:600}.lc-product-post-card-empty{display:flex;align-items:center;color:var(--lc-ppc-muted);font-size:10px}
.lc-product-post-card-tone-neutral{color:var(--lc-ppc-muted)}.lc-product-post-card-tone-positive{color:var(--storm-price-up,var(--lc-ppc-accent))}.lc-product-post-card-tone-negative{color:var(--storm-price-down,var(--storm-text-error))}.lc-product-post-card-tone-warning{color:var(--storm-warning,var(--storm-text-warning))}.lc-product-post-card-tone-info{color:var(--storm-info,var(--storm-text-link))}
.lc-product-post-card-link{position:absolute;z-index:1;inset:0;border-radius:inherit}.lc-product-post-card-link:focus-visible{outline:2px solid var(--ring,var(--lc-ppc-accent));outline-offset:-3px}.lc-product-post-card.is-interactive:has(.lc-product-post-card-link:hover){border-color:color-mix(in srgb,var(--lc-ppc-accent) 45%,var(--lc-ppc-border));transform:translateY(-1px)}.lc-product-post-card{transition:border-color .18s ease,transform .18s ease}.lc-product-post-card-external{position:absolute;z-index:3;right:10px;bottom:10px;width:13px;height:13px;color:var(--lc-ppc-muted);pointer-events:none}
.lc-product-post-card-loading,.lc-product-post-card-locked{position:absolute;z-index:5;inset:0;background:color-mix(in srgb,var(--lc-ppc-surface) 92%,transparent);display:flex;flex-direction:column;justify-content:center;gap:10px;padding:18px}.lc-product-post-card-loading i{height:12px;border-radius:5px;background:var(--muted,var(--storm-background-tertiary));animation:lc-ppc-pulse 1.4s ease-in-out infinite}.lc-product-post-card-loading i:nth-child(2){width:72%}.lc-product-post-card-loading i:nth-child(3){height:110px}.lc-product-post-card-loading i:nth-child(4){width:55%}.lc-product-post-card-locked{align-items:center;color:var(--lc-ppc-muted);font-size:12px}.lc-product-post-card-locked svg{width:22px;height:22px}.lc-product-post-card-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
@keyframes lc-ppc-pulse{0%,100%{opacity:.45}50%{opacity:.9}}
@media(max-width:640px){.lc-product-post-card-rail,.lc-product-post-card-grid{--lc-post-card-height:400px}.lc-product-post-card-rail .lc-product-post-card-slots,.lc-product-post-card-grid .lc-product-post-card-slots{grid-template-rows:auto minmax(56px,1fr) 126px;padding:12px;gap:8px}.lc-product-post-card-rail .lc-product-post-card-slots:has(>.lc-product-post-card-media),.lc-product-post-card-grid .lc-product-post-card-slots:has(>.lc-product-post-card-media){grid-template-rows:auto minmax(56px,1fr) min(116px,var(--lc-post-card-media-height)) 126px}.lc-product-post-card-avatar{--lc-post-card-avatar-size:30px}}
@media(prefers-reduced-motion:reduce){.lc-product-post-card,.lc-product-post-card-loading i,.lc-product-post-card-sparkline-loading{transition:none;animation:none}}
`;

export {
  ProductThemeProvider,
  useProductTheme,
  AvatarStyles,
  deriveAvatarInitials,
  Avatar,
  AvatarFallback,
  AvatarBadge,
  AvatarGroup,
  ProductPostCardStyles,
  ProductPostCardHeader,
  ProductPostCardContent,
  ProductPostCardMedia,
  ProductPostCardAnalytics,
  ProductPostCardInsight,
  ProductPostCardSparkline,
  ProductPostCardMetrics,
  ProductPostCardIndicators,
  ProductPostCard
};
//# sourceMappingURL=chunk-C7VCBV5O.mjs.map